import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  alpha,
} from '@mui/material';
import { motion } from 'framer-motion';

// Import all tab content for search
import FrontendOverviewTab from './FrontendOverviewTab';
import FrontendFeaturesTab from './FrontendFeaturesTab';
import FrontendTechStackTab from './FrontendTechStackTab';
import FrontendInstallationTab from './FrontendInstallationTab';
import FrontendStructureTab from './FrontendStructureTab';
import FrontendUIUXTab from './FrontendUIUXTab';
import FrontendSecurityTab from './FrontendSecurityTab';
import FrontendPerformanceTab from './FrontendPerformanceTab';
import BackendOverviewTab from './BackendOverviewTab';
import BackendTechStackTab from './BackendTechStackTab';
import BackendInstallationTab from './BackendInstallationTab';
import BackendAPIEndpointsTab from './BackendAPIEndpointTab';
import BackendWebSocketTab from './BackendWebSocketTab';
import BackendStructureTab from './BackendStructureTab';
import BackendSecurityTab from './BackendSecurityTab';
import BackendDeploymentTab from './BackendDeploymentTab';

interface SearchResultsTabProps {
  isDark: boolean;
  colors: any;
  discordFont: any;
  glassStyle: any;
  lokiGradient: string;
  goldColor: string;
  isMobile: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

// Search index mapping
const searchIndex = [
  { id: 'frontend-overview', title: 'Overview & Vision', component: FrontendOverviewTab, category: 'Frontend' },
  { id: 'frontend-features', title: 'Key Features', component: FrontendFeaturesTab, category: 'Frontend' },
  { id: 'frontend-tech-stack', title: 'Technology Stack', component: FrontendTechStackTab, category: 'Frontend' },
  { id: 'frontend-installation', title: 'Installation & Setup', component: FrontendInstallationTab, category: 'Frontend' },
  { id: 'frontend-structure', title: 'Project Structure', component: FrontendStructureTab, category: 'Frontend' },
  { id: 'frontend-ui-ux', title: 'UI/UX Design System', component: FrontendUIUXTab, category: 'Frontend' },
  { id: 'frontend-security', title: 'Security Features', component: FrontendSecurityTab, category: 'Frontend' },
  { id: 'frontend-performance', title: 'Performance Optimization', component: FrontendPerformanceTab, category: 'Frontend' },
  { id: 'backend-overview', title: 'Backend Overview', component: BackendOverviewTab, category: 'Backend' },
  { id: 'backend-tech-stack', title: 'Backend Tech Stack', component: BackendTechStackTab, category: 'Backend' },
  { id: 'backend-installation', title: 'Backend Installation', component: BackendInstallationTab, category: 'Backend' },
  { id: 'backend-api-endpoints', title: 'API Endpoints', component: BackendAPIEndpointsTab, category: 'Backend' },
  { id: 'backend-websocket', title: 'WebSocket Integration', component: BackendWebSocketTab, category: 'Backend' },
  { id: 'backend-structure', title: 'Backend Structure', component: BackendStructureTab, category: 'Backend' },
  { id: 'backend-security', title: 'Backend Security', component: BackendSecurityTab, category: 'Backend' },
  { id: 'backend-deployment', title: 'Deployment', component: BackendDeploymentTab, category: 'Backend' },
];

const SearchResultsTab: React.FC<SearchResultsTabProps> = (props) => {
  const filteredResults = searchIndex.filter(item =>
    item.title.toLowerCase().includes(props.searchQuery.toLowerCase()) ||
    item.category.toLowerCase().includes(props.searchQuery.toLowerCase())
  );

  if (filteredResults.length === 0) {
    return (
      <Box>
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 3, 
            ...props.discordFont, 
            color: props.colors.textPrimary,
            fontSize: props.isMobile ? '1.75rem' : '2.5rem',
            textAlign: 'left',
            background: props.lokiGradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Search Results for "{props.searchQuery}"
        </Typography>
        
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: props.colors.cardBg,
            ...props.glassStyle,
            borderRadius: '16px',
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, color: props.colors.textPrimary }}>
            No results found
          </Typography>
          <Typography variant="body2" sx={{ color: props.colors.textSecondary }}>
            Try searching with different keywords
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Show only the first matching result
  const firstResult = filteredResults[0];
  const Component = firstResult.component;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box>
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 3, 
            ...props.discordFont, 
            color: props.colors.textPrimary,
            fontSize: props.isMobile ? '1.75rem' : '2.5rem',
            textAlign: 'left',
            background: props.lokiGradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Search Results for "{props.searchQuery}"
        </Typography>
        
        <Paper
          sx={{
            p: 3,
            mb: 4,
            bgcolor: alpha(props.goldColor, 0.1),
            borderRadius: '16px',
            borderLeft: `4px solid ${props.goldColor}`,
          }}
        >
          <Typography variant="body2" color={props.colors.textPrimary}>
            Showing result: <strong>{firstResult.title}</strong> ({firstResult.category})
          </Typography>
        </Paper>

        <Component {...props} />
      </Box>
    </motion.div>
  );
};

export default SearchResultsTab;